import { computed, ref } from 'vue';
import QprecoInterestsAPI from 'dashboard/api/qprecoInterests';
import { useAlert } from 'dashboard/composables';

/**
 * Ciclo de vida dos links públicos do relatório QPreço: listagem, emissão,
 * cópia e revogação auditada.
 *
 * Extraído de `pages/AreaQprecoIndex.vue` sem alterar o payload enviado à API
 * nem os textos de alerta.
 */
export function useQprecoValuationShares({ activeInterestId, detailInterest }) {
  const valuationShares = ref([]);
  const sharesLoading = ref(false);
  const sharesError = ref(false);
  const creatingShare = ref(false);
  const shareCreateForm = ref({ expiresInDays: '30', viewLimit: '' });
  const issuedShare = ref(null);
  const pendingRevokeShare = ref(null);
  const showRevokeShare = ref(false);
  const revokingShare = ref(false);

  const resetShareState = () => {
    valuationShares.value = [];
    sharesError.value = false;
    sharesLoading.value = false;
    issuedShare.value = null;
    pendingRevokeShare.value = null;
    showRevokeShare.value = false;
  };

  const activeValuationShares = computed(() =>
    valuationShares.value.filter(share => share?.active)
  );
  const inactiveValuationShares = computed(() =>
    valuationShares.value.filter(share => !share?.active)
  );
  const currentIssuedShare = computed(() =>
    issuedShare.value &&
    String(issuedShare.value.qpreco_interest_id) ===
      String(activeInterestId.value)
      ? issuedShare.value
      : null
  );
  const canManageValuationShares = computed(
    () =>
      Boolean(activeInterestId.value) &&
      !detailInterest.value?.external &&
      !String(detailInterest.value?.id || '').startsWith('external:')
  );

  const shareListSections = computed(() => [
    {
      key: 'active',
      title: 'Links ativos',
      empty: 'Nenhum link ativo agora.',
      shares: activeValuationShares.value,
    },
    {
      key: 'inactive',
      title: 'Links inativos',
      empty: 'Nenhum link inativo registrado.',
      shares: inactiveValuationShares.value,
    },
  ]);

  const upsertValuationShare = share => {
    if (!share?.id) return;
    const index = valuationShares.value.findIndex(item => item.id === share.id);
    if (index >= 0) {
      valuationShares.value.splice(index, 1, {
        ...valuationShares.value[index],
        ...share,
      });
    } else {
      valuationShares.value = [share, ...valuationShares.value];
    }
  };

  const fetchValuationShares = async interestId => {
    if (!interestId || String(interestId).startsWith('external:')) {
      resetShareState();
      return;
    }

    sharesLoading.value = true;
    sharesError.value = false;

    try {
      const { data } = await QprecoInterestsAPI.shares(interestId);
      valuationShares.value = Array.isArray(data) ? data : data?.data || [];
    } catch (error) {
      sharesError.value = true;
      valuationShares.value = [];
    } finally {
      sharesLoading.value = false;
    }
  };

  const shareExpirationIso = () => {
    const days = Number.parseInt(shareCreateForm.value.expiresInDays, 10) || 30;
    return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
  };

  const shareCreatePayload = () => {
    const viewLimit = Number.parseInt(shareCreateForm.value.viewLimit, 10);
    return {
      purpose: 'public_report',
      expires_at: shareExpirationIso(),
      view_limit: Number.isFinite(viewLimit) && viewLimit > 0 ? viewLimit : '',
    };
  };

  const createValuationShare = async () => {
    if (!canManageValuationShares.value) return;

    creatingShare.value = true;
    try {
      const { data } = await QprecoInterestsAPI.createShare(
        activeInterestId.value,
        shareCreatePayload()
      );
      issuedShare.value = data;
      upsertValuationShare(data);
      shareCreateForm.value.viewLimit = '';
      useAlert('Link do relatório QPreço criado.');
    } catch (error) {
      useAlert('Não foi possível criar o link agora.');
    } finally {
      creatingShare.value = false;
    }
  };

  const copyIssuedShareUrl = async () => {
    if (
      !currentIssuedShare.value?.public_url ||
      typeof navigator === 'undefined' ||
      !navigator.clipboard
    ) {
      return;
    }
    await navigator.clipboard.writeText(currentIssuedShare.value.public_url);
    useAlert('Link copiado.');
  };

  const requestRevokeShare = share => {
    pendingRevokeShare.value = share;
    showRevokeShare.value = true;
  };

  const cancelRevokeShare = () => {
    if (revokingShare.value) return;
    pendingRevokeShare.value = null;
    showRevokeShare.value = false;
  };

  const confirmRevokeShare = async () => {
    if (!pendingRevokeShare.value?.id || !canManageValuationShares.value)
      return;

    revokingShare.value = true;
    try {
      const { data } = await QprecoInterestsAPI.revokeShare(
        activeInterestId.value,
        pendingRevokeShare.value.id,
        { reason: 'revogado no dashboard QPreço' }
      );
      upsertValuationShare(data);
      if (currentIssuedShare.value?.id === data.id) {
        issuedShare.value = { ...currentIssuedShare.value, ...data };
      }
      useAlert('Compartilhamento revogado.');
      showRevokeShare.value = false;
      pendingRevokeShare.value = null;
    } catch (error) {
      useAlert('Não foi possível revogar este link agora.');
    } finally {
      revokingShare.value = false;
    }
  };

  return {
    valuationShares,
    sharesLoading,
    sharesError,
    creatingShare,
    shareCreateForm,
    showRevokeShare,
    revokingShare,
    activeValuationShares,
    inactiveValuationShares,
    currentIssuedShare,
    canManageValuationShares,
    shareListSections,
    resetShareState,
    fetchValuationShares,
    createValuationShare,
    copyIssuedShareUrl,
    requestRevokeShare,
    cancelRevokeShare,
    confirmRevokeShare,
  };
}
